/** @flow */
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { translate } from 'react-i18next';
import styled from 'styled-components';
import RaisedButton from 'material-ui/RaisedButton';
import { Dialog } from 'common/components/ui/Dialog';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import Campaign from 'common/modules/campaign/domain/Campaign';
import Radio from 'common/domain/radio';
import { MultiSelect, MultiSelectOption } from 'common/components/MultiSelect';
import RadioUser from 'statsradio/domain/RadioUser';

import CampaignAPI from '../services/CampaignAPI';
import { ExternalCampaignListing, sortByName } from '../domain/externalCampaign';
import AuthorisedComponent from '../../authorisation/AuthorisedComponent';
import Permissions from '../../authorisation/Permissions';
import { getToken, getCurrentRadio, getRadioUser } from '../../authentication/session/selectors/sessionSelectors';

const ActionButtons = styled(RaisedButton)`
  margin: 0 ${({ theme }) => theme.metrics.tinyMargin}px; 
`;

function asMultiSelectOptions(externalCampaigns: ExternalCampaignListing[]): MultiSelectOption[] {
  return externalCampaigns.map(ec => ({ label: ec.getName(), value: ec.reference }));
}

export const MESSAGE_ID = 'linking-dialog-message';
export const SELECT_ID = 'external-campaigns-selection';
export const LINK_BUTTON_ID = 'link-campaign-button';

interface Props {
  user: RadioUser;
  radio: Radio;
  token: string;
  campaign: Campaign;
  open: boolean;
  onClose: () => {};
  onLinkSuccess: (linkedExternalCampaigns: ExternalCampaignListing[]) => void;

  actions: {
    openSnackbar: (type: string, message: string) => any,
  },

  t: any;
}

interface State {
  externalsCampaigns: ExternalCampaignListing[],
  selectedExternalCampaigns: MultiSelectOption[],
}

export class LinkCampaignDialog extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      externalsCampaigns: [],
      selectedExternalCampaigns: asMultiSelectOptions(props.campaign.linkedExternalCampaigns),
    };
  }

  componentDidMount() {
    this.fetchExternalCampaigns();
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.radio.isSame(prevProps.radio)) {
      this.fetchExternalCampaigns();
    }
  }

  fetchExternalCampaigns = () => {
    const { radio: { callSign }, token, campaign } = this.props;

    this.setState({ externalsCampaigns: [] });
    CampaignAPI.listExternalCampaigns(token, callSign)
      .then((externals) => {
        const listing = externals.map(e => e.toListing());
        const availableExternalCampaigns = [...listing, ...campaign.linkedExternalCampaigns];
        this.setState({ externalsCampaigns: sortByName(availableExternalCampaigns) });
      });
  };

  onCloseDialog = () => {
    const { campaign, onClose } = this.props;
    onClose();
    this.setState({
      selectedExternalCampaigns: asMultiSelectOptions(campaign.linkedExternalCampaigns),
    });
  }

  async onLinkCampaign() {
    const { actions, t, campaign } = this.props;

    try {
      await this.linkCampaign();
      await actions.openSnackbar('message', t('campaignLinkedSuccessfully'));
    } catch (e) {
      this.setState({ selectedExternalCampaigns: asMultiSelectOptions(campaign.linkedExternalCampaigns) });
      await actions.openSnackbar('error', t('campaignLinkFailed'));
    }

    this.props.onClose();
  }

  async linkCampaign() {
    const { radio: { callSign }, token, campaign, onLinkSuccess } = this.props;
    const { selectedExternalCampaigns, externalsCampaigns } = this.state;

    const selectedReferences = selectedExternalCampaigns.map(s => s.value);
    await CampaignAPI.linkCampaigns(token, callSign, campaign.reference, selectedReferences);

    const linkedExternalCampaigns: ExternalCampaignListing[] = (selectedExternalCampaigns.map(se => externalsCampaigns.find(e => e.reference === se.value)): any);
    onLinkSuccess(linkedExternalCampaigns);
  }

  onSelectLinkCampaigns = (selectedExternalCampaigns: MultiSelectOption[]) => this.setState({ selectedExternalCampaigns });

  render() {
    const { t, open, campaign, user } = this.props;
    const { externalsCampaigns, selectedExternalCampaigns } = this.state;

    const linkPermissions = [Permissions.LINK_EXTERNAL_CAMPAIGNS];
    if (campaign.isRunning()) {
      linkPermissions.push(Permissions.LINK_RUNNING_CAMPAIGN);
    }

    const linkActions = [
      <ActionButtons
        primary
        key="cancel"
        label={t('cancel')}
        onClick={this.onCloseDialog}
      />,
      <AuthorisedComponent permissions={linkPermissions} requireAll key="linkPermissions">
        <ActionButtons
          id={LINK_BUTTON_ID}
          primary
          key="apply"
          label={t('apply')}
          onClick={() => this.onLinkCampaign()}
        />
      </AuthorisedComponent>,
    ];

    const canLinkCampaign = linkPermissions.every(p => user.hasPermission(p));

    return (
      <AuthorisedComponent permissions={Permissions.LINK_EXTERNAL_CAMPAIGNS}>
        <Dialog
          actions={linkActions}
          title={t('linkCampaign')}
          open={open}
          onClose={this.onCloseDialog}
          disableScroll
        >
          <React.Fragment>
            <p id={MESSAGE_ID}>{t(canLinkCampaign ? 'linkCampaignsMessage' : 'cannotLinkRunningCampaignMessage')}</p>
            <MultiSelect
              id={SELECT_ID}
              placeholder={t('selectACampaignOrTypeSomething')}
              noMoreOptionsMessage={t('noMoreExternalCampaignsToSelect')}
              value={selectedExternalCampaigns}
              onChange={this.onSelectLinkCampaigns}
              options={asMultiSelectOptions(externalsCampaigns)}
              multiple
              autocomplete
              disabled={!canLinkCampaign}
            />
          </React.Fragment>
        </Dialog>
      </AuthorisedComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    token: getToken(state),
    radio: getCurrentRadio(state),
    user: getRadioUser(state),
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({ openSnackbar }, dispatch),
  };
}

export default compose(translate('campaigns'), connect(mapStateToProps, mapDispatchToProps))(LinkCampaignDialog);



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/components/LinkCampaignDialog.js